import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";

import { EntryAxios } from "../../axios/entry/entry.axios";
import { EntriesTableSettings } from "../../model/entries-table-settings/EntriesTableSettings";
import { GetUserEntriesTableSettingsDto } from "../../model/entries-table-settings/dto/GetUserEntriesTableSettingsDto";
import { UpdateUserEntriesTableSettingsDto } from "../../model/entries-table-settings/dto/UpdateUserEntriesTableSettingsDto";
import { Entry } from "../../model/entry/Entry";
import { AssignEntryBulkDto } from "../../model/entry/dto/AssignEntryBulkDto";
import { AssignEntryDto } from "../../model/entry/dto/AssignEntryDto";
import { ConvertEntryBulkDto } from "../../model/entry/dto/ConvertEntryBulkDto";
import { CreateEntryDto } from "../../model/entry/dto/CreateNewEntryDto";
import { DeleteEntryBulkDto } from "../../model/entry/dto/DeleteEntryBulkDto";
import { DeleteEntryDto } from "../../model/entry/dto/DeleteEntryDto";
import { ExportEntriesDto } from "../../model/entry/dto/ExportEntriesDto";
import { GetEntryByIdDto } from "../../model/entry/dto/GetEntryByIdDto";
import { GetEntryLegibleForKanbanCardDto } from "../../model/entry/dto/GetEntryLegibleForKanbanCardDto";
import { GetLatestEntriesDto } from "../../model/entry/dto/GetLatestEntriesDto";
import { ImportEntriesDto } from "../../model/entry/dto/ImportEntriesDto";
import { ImportEntriesDtoResponseDto } from "../../model/entry/dto/ImportEntriesResponseDto";
import { RetrieveEntryBulkDto } from "../../model/entry/dto/RetrieveEntryBulkDto";
import { RetrieveEntryDto } from "../../model/entry/dto/RetrieveEntryDto";
import { UpdateEntryDto } from "../../model/entry/dto/UpdateEntryDto";
import { TagEntryBulkDto } from "../../model/entry/dto/TagEntryBulkDto";
import { UpdateEntryTagsDto } from "../../model/entry/dto/UpdateEntryTagsDto";
import { PageDto } from "../../model/meta/PageDto";
import { PageResponseDto } from "../../model/meta/PageResponseDto";

import { AtiraThunk } from "../AtiraThunk";
import { userActions } from "../user/user.slice";
import { MergeEntriesDto } from "../../model/entry/dto/MergeEntriesDto";
import { FindDuplicateEntriesDto } from "../../model/entry/dto/FindDuplicateEntriesDto";
import { FindDuplicateEntriesResponseDto } from "../../model/entry/dto/FindDuplicateEntriesResponseDto";
import { FindDuplicateEntriesByIdDto } from "../../model/entry/dto/FindDuplicateEntriesByIdDto";
import { revertAllReduxAction } from "../store-reset";
import { ConvertEntryDto } from "../../model/entry/dto/ConvertEntryDto";

interface EntryReducer {
  latestEntries: Entry[];
  latestEntriesLoading: boolean;
  latestEntriesMeta: PageDto;
  latestEntriesPendingRequestId?: string;
  //
  entryTableSettings?: EntriesTableSettings;
  //
  entiresLegibleForKanbanCard: Entry[];
  //
  entriesTablePageSize: number;
  entriesTablePage: number;
  //
  customEntryLoadingId: string | null;
  //
  currentEntryLoading: boolean;
}

const initialState = Object.freeze<EntryReducer>({
  latestEntries: [],
  latestEntriesLoading: false,
  latestEntriesMeta: {
    page: 0,
    count: 10,
  },
  latestEntriesPendingRequestId: undefined,
  entryTableSettings: undefined,
  entiresLegibleForKanbanCard: [],
  //
  entriesTablePageSize: 10,
  entriesTablePage: 1,
  //
  customEntryLoadingId: null,
  //
  currentEntryLoading: false,
});

const getEntryById = AtiraThunk<Entry, GetEntryByIdDto>(
  "/entry/:id",
  EntryAxios.getEntryById,
);

const deleteEntry = AtiraThunk<void, DeleteEntryDto>(
  "/entry/delete",
  EntryAxios.deleteEntry,
);

const updateEntryById = AtiraThunk<void, UpdateEntryDto>(
  "/entry/:id/update",
  EntryAxios.updateEntryById,
);

const convertEntryById = AtiraThunk<void, ConvertEntryDto>(
  "/entry/:id/convert",
  EntryAxios.convertEntryById,
);

const createNewEntry = AtiraThunk<void, CreateEntryDto>(
  "/form/submit",
  EntryAxios.createNewEntry,
);

const getLatestEntries = AtiraThunk<
  PageResponseDto<Entry[]>,
  GetLatestEntriesDto
>(`/entry/latest`, EntryAxios.getLatestEntries);

const deleteEntryBulk = AtiraThunk<void, DeleteEntryBulkDto>(
  "/entry/delete/bulk",
  EntryAxios.deleteEntryBulk,
);

const convertEntryBulk = AtiraThunk<void, ConvertEntryBulkDto>(
  "/entry/convert/bulk",
  EntryAxios.convertEntryBulk,
);

const getUserTableSettings = AtiraThunk<
  EntriesTableSettings,
  GetUserEntriesTableSettingsDto
>("/entry/table-settings", EntryAxios.getUserTableSettings);

const updateUserTableSettings = AtiraThunk<
  void,
  UpdateUserEntriesTableSettingsDto
>(`/entry/table-settings/:id`, EntryAxios.updateUserTableSettings);

const getEntriesLegibleForKanbanCard = AtiraThunk<
  Entry[],
  GetEntryLegibleForKanbanCardDto
>("/entry/search/kanban-legible", EntryAxios.getEntriesLegibleForKanbanCard);

const exportEntries = AtiraThunk<string, ExportEntriesDto>(
  "/entry/export",
  EntryAxios.exportEntries,
);

const importEntries = AtiraThunk<
  void | ImportEntriesDtoResponseDto,
  ImportEntriesDto
>("/entry/import", EntryAxios.importEntries);

const assignEntry = AtiraThunk<void, AssignEntryDto>(
  "/entry/assign",
  EntryAxios.assignEntry,
);

const tagEntryBulk = AtiraThunk<void, TagEntryBulkDto>(
  "/entry/tag-bulk",
  EntryAxios.tagEntryBulk,
);

const updateEntryTags = AtiraThunk<void, UpdateEntryTagsDto>(
  "/entry/:id/tags",
  EntryAxios.updateEntryTags,
);

const assignEntryBulk = AtiraThunk<void, AssignEntryBulkDto>(
  "entry/assign/bulk",
  EntryAxios.assignEntryBulk,
);

const retrieveEntry = AtiraThunk<void, RetrieveEntryDto>(
  "/entry/:id/retrieve",
  EntryAxios.retrieveEntry,
);

const retrieveEntryBulk = AtiraThunk<void, RetrieveEntryBulkDto>(
  "/entry/delete/bulk/undo",
  EntryAxios.retrieveEntryBulk,
);

const mergeEntries = AtiraThunk<void, MergeEntriesDto>(
  "/entry/merge",
  EntryAxios.mergeEntries,
);

const findPossibleDuplicates = AtiraThunk<
  FindDuplicateEntriesResponseDto,
  FindDuplicateEntriesDto
>("/entry/data/find-duplicates", EntryAxios.findPossibleDuplicates);

const findDuplicatesEntryById = AtiraThunk<
  FindDuplicateEntriesResponseDto,
  FindDuplicateEntriesByIdDto
>("/entry/data/find-duplicates/:entryId", EntryAxios.findDuplicatesEntryById);

/** The client-only "assignee" column sits third, so the ones after it are pushed back by one. */
const withAssigneeColumn = (
  columns: EntriesTableSettings["columns"] = [],
): EntriesTableSettings["columns"] => [
  ...columns.slice(0, 2),
  {
    enabled: true,
    field: "assignee",
    label: "Assignee",
    toggleAble: false,
    order: 2,
    custom: false,
  },
  ...columns.slice(2).map((col) => ({ ...col, order: col.order + 1 })),
];

const entrySlice = createSlice({
  name: "entry",
  initialState,
  reducers: {
    setEntriesTablePageSize: (state, action) => {
      state.entriesTablePageSize = action.payload;
    },

    setEntriesTablePage: (state, action) => {
      state.entriesTablePage = action.payload;
    },

    setCustomEntryLoadingId: (state, action) => {
      state.customEntryLoadingId = action.payload;
    },

    /** The columns the API now holds after a successful update, so they needn't be fetched again. */
    setEntryTableSettingsColumns: (
      state,
      action: PayloadAction<EntriesTableSettings["columns"]>,
    ) => {
      if (state.entryTableSettings) {
        // The API numbers them by their position
        state.entryTableSettings.columns = withAssigneeColumn(
          action.payload.map((column, order) => ({ ...column, order })),
        );
      }
    },
  },
  extraReducers: ({ addCase, addMatcher }) => {
    addCase(getEntryById.pending, (state, action) => {
      state.currentEntryLoading = true;
    });
    addCase(getEntryById.fulfilled, (state, action) => {
      let entryAlreadyStored;

      state.latestEntries = state.latestEntries.map((ent) => {
        if (ent._id === action.meta.arg.entryId) {
          entryAlreadyStored = true;
          return action.payload;
        }
        return ent;
      });

      if (!entryAlreadyStored) {
        state.latestEntries.push(action.payload);
      }
      state.currentEntryLoading = false;
    });
    addCase(getEntryById.rejected, (state, action) => {
      state.currentEntryLoading = false;
    });

    addCase(getLatestEntries.pending, (state, action) => {
      state.latestEntriesPendingRequestId = action.meta.requestId;
      state.latestEntriesLoading = true;
    });
    addCase(getLatestEntries.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestEntriesPendingRequestId) {
        return;
      }

      state.latestEntriesMeta = action.payload.meta;
      state.latestEntriesLoading = false;
      state.latestEntries = action.payload.data || [];
      state.latestEntriesPendingRequestId = undefined;
    });
    addCase(getLatestEntries.rejected, (state, action) => {
      if (action.meta.requestId !== state.latestEntriesPendingRequestId) {
        return;
      }

      state.latestEntriesLoading = false;
      state.latestEntriesPendingRequestId = undefined;
    });

    addCase(getUserTableSettings.fulfilled, (state, action) => {
      state.entryTableSettings = {
        ...action.payload,
        columns: withAssigneeColumn(action.payload.columns),
      };
    });

    addCase(getEntriesLegibleForKanbanCard.fulfilled, (state, action) => {
      state.entiresLegibleForKanbanCard = action.payload;
    });

    addMatcher(isAnyOf(userActions.logout, revertAllReduxAction), (state) => {
      return (state = initialState);
    });
  },
});

export const entryActions = {
  ...entrySlice.actions,
  deleteEntry,
  getEntryById,
  updateEntryById,
  createNewEntry,
  getLatestEntries,
  deleteEntryBulk,
  convertEntryBulk,
  getUserTableSettings,
  updateUserTableSettings,
  getEntriesLegibleForKanbanCard,
  exportEntries,
  importEntries,
  assignEntry,
  assignEntryBulk,
  updateEntryTags,
  tagEntryBulk,
  retrieveEntry,
  retrieveEntryBulk,
  mergeEntries,
  findPossibleDuplicates,
  findDuplicatesEntryById,
  convertEntryById,
};

export default entrySlice.reducer;
